import type {
  ProviderNotificationClient,
  ProviderNotificationSnapshot,
} from '../../contracts/ProviderNotificationClient'
import { createVideoServiceHttp, isAuthFailure } from './videoServiceHttp'

type NestApi = {
  get: (url: string) => Promise<unknown>
  post: (url: string, body?: unknown) => Promise<unknown>
}

export function createGetStreamProviderNotificationClient(params: {
  nestApi: NestApi
  getCareAccessToken: () => string | null | undefined
}): ProviderNotificationClient {
  const http = createVideoServiceHttp(params)

  const client: ProviderNotificationClient = {
    async getNotifications() {
      await http.ensureIdentity()
      return http.careJson<ProviderNotificationSnapshot>(
        '/provider/notifications'
      )
    },

    subscribe(onUpdate, onError) {
      let cancelled = false
      let abort: AbortController | null = null
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null
      let failures = 0

      const connect = async () => {
        if (cancelled) {
          return
        }

        try {
          await http.ensureIdentity()
          const token = params.getCareAccessToken()

          if (!token) {
            throw new Error('Care access token is missing.')
          }

          const base = await http.resolveApiBase()
          abort = new AbortController()
          const response = await fetch(
            `${base}/provider/notifications/stream`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'text/event-stream',
              },
              signal: abort.signal,
            }
          )

          if (!response.ok || !response.body) {
            throw new Error(`Notification stream failed (${response.status})`)
          }

          failures = 0
          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          while (!cancelled) {
            const { done, value } = await reader.read()

            if (done) {
              break
            }

            buffer += decoder.decode(value, { stream: true })
            const chunks = buffer.split('\n\n')
            buffer = chunks.pop() || ''

            for (const chunk of chunks) {
              const dataLine = chunk
                .split('\n')
                .find((line) => line.startsWith('data:'))

              if (!dataLine) {
                continue
              }

              try {
                onUpdate(
                  JSON.parse(
                    dataLine.replace(/^data:\s?/, '')
                  ) as ProviderNotificationSnapshot
                )
              } catch {
                // Ignore malformed frames; the next snapshot is authoritative.
              }
            }
          }
        } catch (error) {
          if (
            cancelled ||
            (error as Error)?.name === 'AbortError' ||
            isAuthFailure(error)
          ) {
            return
          }

          onError?.(error as Error)
        }

        if (!cancelled) {
          failures += 1
          reconnectTimer = setTimeout(
            () => void connect(),
            Math.min(30_000, 2_000 * 2 ** Math.min(failures, 4))
          )
        }
      }

      void connect()

      return () => {
        cancelled = true
        abort?.abort()

        if (reconnectTimer) {
          clearTimeout(reconnectTimer)
        }
      }
    },

    async markRead(notificationId) {
      await http.ensureIdentity()
      await http.careJson(
        `/provider/notifications/${encodeURIComponent(notificationId)}/read`,
        { method: 'POST', body: '{}' }
      )
    },

    async markAllRead() {
      await http.ensureIdentity()
      await http.careJson('/provider/notifications/read-all', {
        method: 'POST',
        body: '{}',
      })
    },
  }

  return client
}
